import { useEffect, useMemo, useState } from 'react'
import { useI18n } from '../../i18n'
import styles from './AddToHomeScreen.module.css'

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false
  const mq = window.matchMedia('(display-mode: standalone)').matches
  const iosStandalone =
    'standalone' in navigator &&
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone)
  return mq || iosStandalone
}

function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  const iOS = /iPad|iPhone|iPod/.test(ua)
  const iPadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
  return iOS || iPadOs
}

/** Phones and tablets only — desktop/laptop install help is not useful here. */
function isMobileOrTablet(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false
  if (isIosDevice()) return true
  if (/Android/i.test(navigator.userAgent)) return true
  // Other touch-first tablets (e.g. Windows) without a phone/tablet UA.
  const coarsePointer = window.matchMedia('(pointer: coarse)').matches
  return coarsePointer && navigator.maxTouchPoints > 1
}

type Props = {
  className?: string
}

/**
 * Landing-page help for installing / bookmarking the PWA to the Home Screen.
 * Shown only on phones/tablets. Hidden when already installed (standalone /
 * `appinstalled`), and never shown on desktop or laptop browsers.
 */
export function AddToHomeScreen({ className }: Props) {
  const { t } = useI18n()
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(isStandaloneDisplay)
  const [busy, setBusy] = useState(false)
  const ios = useMemo(() => isIosDevice(), [])
  const handheld = useMemo(() => isMobileOrTablet(), [])

  useEffect(() => {
    if (!handheld) return

    const onChange = () => setInstalled(isStandaloneDisplay())
    const mq = window.matchMedia('(display-mode: standalone)')
    mq.addEventListener?.('change', onChange)

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault()
      setDeferred(event as BeforeInstallPromptEvent)
    }
    const onInstalled = () => {
      setInstalled(true)
      setDeferred(null)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      mq.removeEventListener?.('change', onChange)
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [handheld])

  const handleInstall = async () => {
    if (!deferred) return
    setBusy(true)
    try {
      await deferred.prompt()
      await deferred.userChoice
      setDeferred(null)
      setInstalled(isStandaloneDisplay())
    } finally {
      setBusy(false)
    }
  }

  if (!handheld || installed) return null

  return (
    <section
      className={`${styles.root} ${className ?? ''}`}
      aria-labelledby="home-screen-heading"
    >
      <h2 id="home-screen-heading" className={styles.title}>
        {t('library.homeScreenTitle')}
      </h2>
      <p className={styles.body}>{t('library.homeScreenBody')}</p>

      {deferred && (
        <button
          type="button"
          className={`btn btn-primary ${styles.installBtn}`}
          disabled={busy}
          onClick={() => void handleInstall()}
        >
          {busy ? t('library.homeScreenInstalling') : t('library.homeScreenInstall')}
        </button>
      )}

      <ol className={styles.steps}>
        {ios ? (
          <>
            <li>{t('library.homeScreenIosStep1')}</li>
            <li>{t('library.homeScreenIosStep2')}</li>
            <li>{t('library.homeScreenIosStep3')}</li>
          </>
        ) : (
          <>
            <li>{t('library.homeScreenAndroidStep1')}</li>
            <li>{t('library.homeScreenAndroidStep2')}</li>
            <li>{t('library.homeScreenAndroidStep3')}</li>
          </>
        )}
      </ol>
    </section>
  )
}
